import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import MainCard from "components/MainCard";
import { useNavigate, useParams } from "react-router-dom";
import { FineSchema, IFineSettings } from "../api/types";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ApiResponse } from "common/models/ApiResponse";
import { showError, showSuccess } from "utils/showMessages";
import { showServerErrors } from "utils/formUtils";
import { Button } from "@mui/material";
import Loader from "components/Loader";
import { InfoCircleFilled } from "@ant-design/icons";
import { UpdateFineSettings } from "../api/mutation";
import { GetFineSettings } from "../api/hooks";

const FineSettingsTab = () => {
  const { libraryID } = useParams();
  const editMode = libraryID !== undefined;
  const queryClient = useQueryClient();

  const navigateTo = useNavigate();
  const updateMutation = UpdateFineSettings();
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<IFineSettings>({
    resolver: zodResolver(FineSchema),
  });

  const { data: getFineSettingsQuery, isFetching } = GetFineSettings(
    libraryID || "",
  );

  const [isApplyFineChecked, setIsApplyFineChecked] = useState(false);
  const [isAutoReceiptOnFineChecked, setIsAutoReceiptOnFineChecked] =
    useState(false);
  // const [fineSettings, setFineSettings] = useState<IFineSettings>();
  const [isExemptFineonHolidayChecked, setIsExemptFineonHolidayChecked] =
    useState(false);
  const [isCapFineAtMaterialCostChecked, setIsCapFineAtMaterialCostChecked] =
    useState(false);
  const [
    isRestrictIssueOnOutstandingFineChecked,
    setIsRestrictIssueOnOutstandingFineChecked,
  ] = useState(false);

  useEffect(() => {
    if (getFineSettingsQuery) {
      // setFineSettings(getFineSettingsQuery.data.apiData);
      reset(getFineSettingsQuery?.apiData);
      setIsApplyFineChecked(getFineSettingsQuery?.apiData?.isApplyFine);
      setIsAutoReceiptOnFineChecked(
        getFineSettingsQuery?.apiData?.isAutoReceiptOnFine,
      );
      setIsExemptFineonHolidayChecked(
        getFineSettingsQuery?.apiData?.isExemptFineOnHoliday,
      );
      setIsCapFineAtMaterialCostChecked(
        getFineSettingsQuery?.apiData?.isCapFineAtMaterialCost,
      );
      setIsRestrictIssueOnOutstandingFineChecked(
        getFineSettingsQuery?.apiData?.isRestrictIssueOnOutstandingFine,
      );
    }
  }, [getFineSettingsQuery, reset]);

  const onSubmit = handleSubmit((data) => {
    if (isSubmitting) return;
    data.libraryID = libraryID || "";

    updateMutation.mutate(data, {
      onSettled(response) {
        handleResponse(response);
      },
    });
  });

  const handleIsApplyFineChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsApplyFineChecked(event.target.checked);
  };

  const handleIsAutoReceiptOnFineChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsAutoReceiptOnFineChecked(event.target.checked);
  };

  const handleIsExemptFineonHolidayChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsExemptFineonHolidayChecked(event.target.checked);
  };

  const handleIsCapFineAtMaterialCostChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsCapFineAtMaterialCostChecked(event.target.checked);
  };

  const handleIsRestrictIssueOnOutstandingFineChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsRestrictIssueOnOutstandingFineChecked(event.target.checked);
  };

  const handleResponse = async (response: ApiResponse<number> | undefined) => {
    if (response?.status == 200) {
      if (editMode) {
        queryClient.invalidateQueries({
          queryKey: ["library", "getFineSettings", "FineSettings"],
        });
      }
      showSuccess(`Record ${editMode ? "updated" : "added"} successfully`);
      navigateTo(`../edit/${libraryID}/fine`);
    } else {
      if (response?.status == 404) {
        showError(response?.title ?? "");
      } else if (response?.status == 400) {
        if (response?.modelErrors) {
          showServerErrors(setError, response.errors);
          showError(response?.title ?? "");
        } else {
          showError(response?.title ?? "");
        }
      } else {
        showError(response?.title ?? "");
      }
    }
  };

  return (
    <>
      <MainCard
        content={false}
        darkTitle
        darktitleVariant={"h4"}
        title={editMode ? "Fine Settings" : "Add Library"}
      >
        <Box component={"form"} sx={{ px: 2.5 }} onSubmit={onSubmit}>
          {isFetching ? <Loader /> : <></>}

          <Grid container spacing={2} direction={"row"}>
            <Grid
              container
              item
              xs={12}
              md={6}
              lg={6}
              alignItems="center"
              my={2}
            >
              <Grid item xs={6} sm={6} md={9} lg={10}>
                <InputLabel htmlFor="isApplyFine" sx={{ fontWeight: 600 }}>
                  <Stack direction={"row"} alignItems={"center"} spacing={1}>
                    <Stack>Do you want to apply fine?</Stack>
                    <Stack>
                      <Tooltip
                        title="Fine will apply in Issued Accessions (If Enabled)"
                        arrow
                      >
                        <InfoCircleFilled />
                      </Tooltip>
                    </Stack>
                  </Stack>
                </InputLabel>
              </Grid>
              <Grid item xs={2} sm={2} md={3} lg={2}>
                <Switch
                  {...register("isApplyFine")}
                  checked={isApplyFineChecked}
                  onChange={handleIsApplyFineChange}
                  id="isApplyFine"
                  inputProps={{ "aria-label": "controlled" }}
                />
              </Grid>
            </Grid>
            <Grid
              container
              item
              xs={12}
              md={6}
              lg={6}
              alignItems="center"
              my={2}
            >
              <Grid item xs={6} sm={6} md={9} lg={10}>
                <InputLabel
                  htmlFor="isAutoReceiptOnFine"
                  sx={{ fontWeight: 600 }}
                >
                  <Stack direction={"row"} alignItems={"center"} spacing={1}>
                    <Stack>
                      Do you want to apply auto receipt on the fine?
                    </Stack>
                    <Stack>
                      <Tooltip
                        title="a checkbox will appear for fine collection at return over due material in issue return screen (If Enabled)"
                        arrow
                      >
                        <InfoCircleFilled />
                      </Tooltip>
                    </Stack>
                  </Stack>
                </InputLabel>
              </Grid>
              <Grid item xs={2} sm={2} md={3} lg={2}>
                <Switch
                  {...register("isAutoReceiptOnFine")}
                  checked={isAutoReceiptOnFineChecked}
                  onChange={handleIsAutoReceiptOnFineChange}
                  id="isAutoReceiptOnFine"
                  inputProps={{ "aria-label": "controlled" }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2} direction={"row"}>
            <Grid
              container
              item
              xs={12}
              md={6}
              lg={6}
              alignItems="center"
              my={2}
              mt={1}
            >
              <Grid item xs={6} sm={6} md={9} lg={10}>
                <InputLabel
                  htmlFor="isExemptFineOnHoliday"
                  sx={{ fontWeight: 600 }}
                >
                  <Stack direction={"row"} alignItems={"center"} spacing={1}>
                    <Stack>
                      Do you want to apply the fine exemption on holidays?
                    </Stack>
                    <Stack>
                      <Tooltip
                        title="Fine will not apply in holidays (If Enabled)"
                        arrow
                      >
                        <InfoCircleFilled />
                      </Tooltip>
                    </Stack>
                  </Stack>
                </InputLabel>
              </Grid>
              <Grid item xs={2} sm={2} md={3} lg={2}>
                <Switch
                  {...register("isExemptFineOnHoliday")}
                  checked={isExemptFineonHolidayChecked}
                  onChange={handleIsExemptFineonHolidayChange}
                  id="isExemptFineOnHoliday"
                  inputProps={{ "aria-label": "controlled" }}
                />
              </Grid>
            </Grid>
            <Grid
              container
              item
              xs={12}
              md={6}
              lg={6}
              alignItems="center"
              my={2}
              mt={1}
            >
              <Grid item xs={6} sm={6} md={9} lg={10}>
                <InputLabel
                  htmlFor="isCapFineAtMaterialCost"
                  sx={{ fontWeight: 600 }}
                >
                  <Stack direction={"row"} alignItems={"center"} spacing={1}>
                    <Stack>Do you want to cap the fine at book cost?</Stack>
                    <Stack>
                      <Tooltip
                        title="Fine will not exceed the accession's price (or the material's price if the accession has none), if a price is set (If Enabled)"
                        arrow
                      >
                        <InfoCircleFilled />
                      </Tooltip>
                    </Stack>
                  </Stack>
                </InputLabel>
              </Grid>
              <Grid item xs={2} sm={2} md={3} lg={2}>
                <Switch
                  {...register("isCapFineAtMaterialCost")}
                  checked={isCapFineAtMaterialCostChecked}
                  onChange={handleIsCapFineAtMaterialCostChange}
                  id="isCapFineAtMaterialCost"
                  inputProps={{ "aria-label": "controlled" }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2} direction={"row"}>
            <Grid
              container
              item
              xs={12}
              md={6}
              lg={6}
              alignItems="center"
              my={2}
              mt={1}
            >
              <Grid item xs={6} sm={6} md={9} lg={10}>
                <InputLabel
                  htmlFor="isRestrictIssueOnOutstandingFine"
                  sx={{ fontWeight: 600 }}
                >
                  <Stack direction={"row"} alignItems={"center"} spacing={1}>
                    <Stack>
                      Do you want to block issue on outstanding fine?
                    </Stack>
                    <Stack>
                      <Tooltip
                        title="Block issuing a new item to a member who currently has an overdue book, or whose outstanding fine exceeds the Max Allowed Fine For New Issue (If Enabled)"
                        arrow
                      >
                        <InfoCircleFilled />
                      </Tooltip>
                    </Stack>
                  </Stack>
                </InputLabel>
              </Grid>
              <Grid item xs={2} sm={2} md={3} lg={2}>
                <Switch
                  {...register("isRestrictIssueOnOutstandingFine")}
                  checked={isRestrictIssueOnOutstandingFineChecked}
                  onChange={handleIsRestrictIssueOnOutstandingFineChange}
                  id="isRestrictIssueOnOutstandingFine"
                  inputProps={{ "aria-label": "controlled" }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="phase1FineDays" sx={{ mb: "-5px" }}>
                Phase 1 Fine Days <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.phase1FineDays}>
                <TextField
                  placeholder="Enter Phase 1 Fine Days"
                  {...register("phase1FineDays")}
                  id="phase1FineDays"
                  margin="normal"
                  error={!!errors.phase1FineDays}
                  helperText={
                    errors.phase1FineDays ? errors.phase1FineDays?.message : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="phase1FineCharge" sx={{ mb: "-5px" }}>
                Phase 1 Fine Charge <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.phase1FineCharge}>
                <TextField
                  placeholder="Enter Phase 1 Fine Charge"
                  {...register("phase1FineCharge")}
                  id="phase1FineCharge"
                  margin="normal"
                  error={!!errors.phase1FineCharge}
                  helperText={
                    errors.phase1FineCharge
                      ? errors.phase1FineCharge?.message
                      : ""
                  }
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="phase2FineDays" sx={{ mb: "-5px" }}>
                Phase 2 Fine Days <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.phase2FineDays}>
                <TextField
                  placeholder="Enter Phase 2 Fine Days"
                  {...register("phase2FineDays")}
                  id="phase2FineDays"
                  margin="normal"
                  error={!!errors.phase2FineDays}
                  helperText={
                    errors.phase2FineDays ? errors.phase2FineDays?.message : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="phase2FineCharge" sx={{ mb: "-5px" }}>
                Phase 2 Fine Charge <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.phase2FineCharge}>
                <TextField
                  placeholder="Enter Phase 2 Fine Charge"
                  {...register("phase2FineCharge")}
                  id="phase2FineCharge"
                  margin="normal"
                  error={!!errors.phase2FineCharge}
                  helperText={
                    errors.phase2FineCharge
                      ? errors.phase2FineCharge?.message
                      : ""
                  }
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="defaultCharge" sx={{ mb: "-5px" }}>
                Default Charge <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!errors.defaultCharge}>
                <TextField
                  placeholder="Enter Issue Max Items"
                  {...register("defaultCharge")}
                  id="defaultCharge"
                  margin="normal"
                  error={!!errors.defaultCharge}
                  helperText={
                    errors.defaultCharge ? errors.defaultCharge?.message : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <InputLabel htmlFor="maxAllowedFineForIssue" sx={{ mb: "-5px" }}>
                Max Allowed Fine For New Issue
                <Tooltip
                  title="When user have pending fine,
                this will be the max fine limit for new issue"
                  arrow
                >
                  <InfoCircleFilled />
                </Tooltip>{" "}
              </InputLabel>
              <FormControl fullWidth error={!!errors.maxAllowedFineForIssue}>
                <TextField
                  placeholder="Enter Max Allowed Fine For New Issue"
                  {...register("maxAllowedFineForIssue")}
                  id="maxAllowedFineForIssue"
                  margin="normal"
                  error={!!errors.maxAllowedFineForIssue}
                  helperText={
                    errors.maxAllowedFineForIssue
                      ? errors.maxAllowedFineForIssue?.message
                      : ""
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container item spacing={2} mb={2} pt={1}>
            <Grid item sm={12}>
              <Button
                title={editMode ? "Update" : "Save"}
                type="submit"
                disabled={isSubmitting}
                variant="contained"
              >
                {isSubmitting && (
                  <CircularProgress
                    color="inherit"
                    size="1rem"
                    sx={{ mr: 1 }}
                  />
                )}
                {editMode ? "Update" : "Save"}
              </Button>
              <Button
                title="Back"
                sx={{ ml: 1 }}
                variant="contained"
                color="secondary"
                disabled={isSubmitting}
                onClick={() => navigateTo(`/admin/library`)}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        </Box>
      </MainCard>
    </>
  );
};
export default FineSettingsTab;
